import { Inject, Injectable } from '@angular/core';

import { BetslipConfig } from '@frontend/sports/common/client-config-data-access';
import { Nullable } from '@frontend/sports/common/core/utils/extended-types';
import { interpolateDoubleBracesString } from '@frontend/sports/common/core/utils/string';
import { Store } from '@ngrx/store';
import { Observable, combineLatest, forkJoin, map } from 'rxjs';

import { IBetslipMessages } from '../../../../../base/models/betslip-messages';
import {
    BETSLIP_MESSAGES_PROVIDER_INJECTION_TOKEN,
    IBetslipMessagesProviderService,
} from '../../../../../base/services/betslip-messages-provider.service';
import { IBetslipRootState } from '../../../../../base/store/state';
import { ResultStatus } from '../../../../../core/picks/pick-models';
import { IFailedSlipResult, IPlacedBetslipResult } from '../../../../betplacement/models';
import { isFailedSlip, isSucceededSlip } from '../../../../betplacement/utils';
import PickTypeSelectorService from '../../../../picks/services/pick-type-selector.service';
import { singleBetStateSelector } from '../../../../single-bet/selectors';
import { ISingleBetState } from '../../../../single-bet/state';
import { BetPlacementError } from '../../../../validation/errors/bet-placement-error';
import { BetPlacementErrorIcon } from '../../../../validation/errors/bet-placement-error-icon';
import { BetslipError } from '../../../../validation/errors/betslip-error';
import { BetslipErrorMessageFormatterService } from '../../../../validation/services/formatting/betslip-error-message-formatter.service';
import { isFinishedError, isPickStatusClosed, isPickStatusLocked } from '../../../../validation/services/utils/betslip-errors-utils';
import { betslipPlacedResultStateSelector } from '../../../selectors';
import { FailedSlipErrorViewModel, FailedSlipViewModel } from './result-failed-slip.model';

export interface FailedResultMessages {
    message?: string;
    failedInfo?: string;
    remainingText?: string;
}

@Injectable()
export class FailedResultService {
    messages: IBetslipMessages;
    hideStraightBetDeclined = false;
    constructor(
        private pickTypeSelector: PickTypeSelectorService,
        private store: Store<IBetslipRootState>,
        private errorMessageFormatter: BetslipErrorMessageFormatterService,
        @Inject(BETSLIP_MESSAGES_PROVIDER_INJECTION_TOKEN) messageProvider: IBetslipMessagesProviderService,
        private betslipConfig: BetslipConfig,
    ) {
        this.messages = messageProvider.getMessages();
        this.hideStraightBetDeclined = this.betslipConfig?.hideStraightBetDeclined;
    }

    getFailedResultModel(): Observable<{
        slips: FailedSlipViewModel[];
        info: Nullable<FailedResultMessages>;
    }> {
        return combineLatest([this.store.select(betslipPlacedResultStateSelector), this.store.select(singleBetStateSelector)]).pipe(
            map(([result, singlePicks]) => {
                return { slips: this.getFailedSlipViewModel(result), info: this.getInfoMessage(result, singlePicks) };
            }),
        );
    }

    private getInfoMessage(result: IPlacedBetslipResult, singleBets: ISingleBetState): FailedResultMessages | null {
        const failed = result.slips.filter(isFailedSlip);
        const unselectedPicksCount = Object.values(singleBets.picks).filter((t) => !t.isSelected).length + failed.length;
        let remaining;

        if (unselectedPicksCount > 0) {
            remaining = interpolateDoubleBracesString(this.messages.common.Remains, {
                count: unselectedPicksCount,
            });
        }

        if (failed.length > 0) {
            if (result.slips.filter(isSucceededSlip).length > 0) {
                return this.hideStraightBetDeclined
                    ? { remainingText: remaining }
                    : {
                          message: interpolateDoubleBracesString(this.messages.common.SingleDeclined, {
                              count: failed.length,
                          }),
                          remainingText: remaining,
                      };
            } else {
                const failedSlip = <IFailedSlipResult>result.slips[0];

                return {
                    message: <string>this.messages.common[(failedSlip.betType + 'NotAccepted') as keyof typeof this.messages.common],
                    failedInfo: this.messages.common.ReviewYourBetslip,
                };
            }
        }

        if (remaining) {
            return {
                remainingText: remaining,
            };
        }

        return null;
    }

    private getFailedSlipViewModel(result: IPlacedBetslipResult): FailedSlipViewModel[] {
        return result.slips.filter(isFailedSlip).map((slip) => {
            return {
                picks: Object.values(slip.picks).map((pick) => {
                    const [pickTemplate, pickClass] = this.pickTypeSelector.resolve(pick.clientPick);
                    const isLocked = !!pick.errors.find((e) => isPickStatusLocked(e) || isPickStatusClosed(e));
                    const isFinished = !!pick.errors.find(isFinishedError);
                    pick.clientPick.resultStatus = isLocked || isFinished ? ResultStatus.Unknown : ResultStatus.Open;

                    return {
                        pick: pick.clientPick,
                        errors: this.combineErrorsStream(pick.errors),
                        template: pickTemplate,
                        pickClass,
                        pick$: this.store.select(
                            (s) => s.betslip.picks.pickList.find((p) => p.id.toString() === pick.clientPick.id.toString()) || pick.clientPick,
                        ),
                        isFinished,
                        isLocked,
                    };
                }),
                betslipErrors$: this.combineErrorsStream(slip.errors),
            };
        });
    }

    private combineErrorsStream(errors: BetPlacementError[]) {
        return forkJoin(errors.map(this.mapError));
    }

    private mapError = (err: BetslipError): Observable<FailedSlipErrorViewModel> =>
        this.errorMessageFormatter.getErrorMessage(err).pipe(
            map((format) => {
                return {
                    message: typeof format === 'string' ? format : format?.message || err.type,
                    warning: err.icon === BetPlacementErrorIcon.Warning,
                    type: err.type,
                };
            }),
        );
}
